import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { useBroadcastEditContext } from "./BroadcastEditContainer";
import { toastError } from "../../../shared/toastHelper";
import { heatmapKey, paintLinkHeatmap } from "./heatmapUtils";

const css = `
.bo-heat-legend { display:flex; align-items:center; gap:8px; font-size:12px; color:var(--sl-text-tertiary); }
.bo-heat-scale { width:120px; height:10px; border-radius:5px; background:linear-gradient(90deg, hsl(140,85%,48%), hsl(70,85%,48%), hsl(0,85%,48%)); }
.bo-heat-frame { width:100%; height:60vh; border:none; background:#FFFFFF; }
.bo-heat-body { padding:12px 24px 24px; }
`;

const HeatmapCard = () => {
  const { csrfToken, team, template, sampleData, beefreeTemplate, venueOwnership, overviewLinkClicks } =
    useBroadcastEditContext();

  const [html, setHtml] = useState("");
  const iframeRef = useRef(null);

  const links = overviewLinkClicks || [];
  const maxClicks = useMemo(() => links.reduce((max, link) => Math.max(max, link.clicks), 0), [links]);
  const stats = useMemo(() => new Map(links.map((link) => [heatmapKey(link.url), link])), [links]);

  useEffect(() => {
    if (!beefreeTemplate?.id || links.length === 0) return undefined;
    let ignore = false;
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios
      .post(`/teams/${team.id}/beefree_templates/${beefreeTemplate.id}/render_liquid_html`, {
        beefree_template: { template, sample_data: sampleData, venue_ownership_id: venueOwnership?.id },
      })
      .then(({ data }) => {
        if (!ignore) setHtml(data.parsedText);
      })
      .catch(() => {
        if (!ignore) toastError("Could not load the email for the heatmap, please try again");
      });
    return () => {
      ignore = true;
    };
  }, [beefreeTemplate?.id, links.length, template, sampleData]);

  const paintHeatmap = () => paintLinkHeatmap(iframeRef.current?.contentDocument, stats, maxClicks);

  return (
    <div className="bo-card">
      <style>{css}</style>
      <div className="bo-card-head">
        <div>
          <h3 className="bo-card-title">Heatmap</h3>
          <div className="bo-card-sub">Links shaded and badged by how many clicks they drew</div>
        </div>
        <div className="bo-heat-legend">
          <span>Fewer</span>
          <span className="bo-heat-scale" />
          <span>More</span>
        </div>
      </div>

      {links.length === 0 ? (
        <div className="bo-empty">No link clicks tracked yet</div>
      ) : (
        <div className="bo-heat-body">
          <iframe
            ref={iframeRef}
            className="bo-heat-frame"
            srcDoc={html}
            sandbox="allow-same-origin"
            onLoad={paintHeatmap}
            title="Email heatmap"
          />
        </div>
      )}
    </div>
  );
};

export default HeatmapCard;
